import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { toast } from 'sonner'

import { usePageScrollToTop } from '@/components/sidebar'
import { SkeletonRows } from '@/components/ui/skeleton'
import { CacheKeys } from '@/lib/cache'
import { parseError } from '@/lib/errors'
import {
  createPolicyChange,
  type PolicyChangeKind,
  type PolicyChangeRequest,
  usePolicyChange,
} from '@/models/policyChange'
import { type Org, useCurrentUser } from '@/models/user'
import { ClaimStepper } from '../Claims/ClaimStepper'
import { policiesFromOrg } from './PoliciesIncluded'
import { StepDates } from './StepDates'
import { StepDetails } from './StepDetails'
import { StepReview } from './StepReview'
import { StepWhatsChanging } from './StepWhatsChanging'
import { WithdrawRequest } from './WithdrawRequest'

const STEPS = ["What's changing", 'Details', 'Effective dates', 'Review & send'] as const

/**
 * The guided request: a draft is created when the customer leaves step 1, then saved change by change. With
 * forCustomer, staff build it in the console for the given org and send it unsigned.
 */
export function PolicyChangeForm({
  request: initial,
  forCustomer,
}: {
  request?: PolicyChangeRequest
  /** The customer staff are building the request for; absent when the customer builds their own. */
  forCustomer?: Pick<Org, 'id' | 'currentPolicies'>
}) {
  const queryClient = useQueryClient()
  const { user } = useCurrentUser({ enabled: true })
  const org = forCustomer ?? user?.currentOrg
  const orgId = org?.id
  const [requestId, setRequestId] = useState(initial?.id)
  const { data } = usePolicyChange(orgId, requestId)
  const request = data ?? initial
  // A draft that already has changes, such as one from the Trucks page, opens on its details.
  const [activeStep, setActiveStep] = useState(initial?.changes.length ? 2 : 1)
  const [kinds, setKinds] = useState<PolicyChangeKind[]>(() => [
    ...new Set(initial?.changes.map((line) => line.kind)),
  ])
  const [creating, setCreating] = useState(false)

  const scrollToTop = usePageScrollToTop()
  useEffect(() => {
    scrollToTop()
  }, [activeStep, scrollToTop])

  async function leaveFirstStep() {
    if (requestId || !orgId) {
      setActiveStep(2)
      return
    }
    setCreating(true)
    try {
      const created = await createPolicyChange(orgId)
      queryClient.setQueryData([CacheKeys.POLICY_CHANGES, orgId, created.id], created)
      setRequestId(created.id)
      // The wizard keeps its state; only the address changes, so a reload lands on the draft.
      const draftPath = forCustomer
        ? `/insure/internal/customers/${orgId}/policy-changes/${created.id}`
        : `/insure/policy-changes/${created.id}`
      window.history.replaceState(window.history.state, '', draftPath)
      setActiveStep(2)
    } catch (error) {
      toast.error(parseError(error))
    } finally {
      setCreating(false)
    }
  }

  if (!user || !org || !orgId) return <SkeletonRows className="mt-10" rows={6} />
  return (
    <>
      <Helmet>
        <title>{forCustomer ? 'Start a policy change' : 'Request a policy change'}</title>
      </Helmet>
      <div className="mx-auto flex w-full max-w-6xl flex-col md:flex-row md:gap-8">
        <aside className="hidden shrink-0 md:sticky md:top-4 md:block md:w-44 md:self-start md:px-4 md:py-10">
          <ClaimStepper
            steps={STEPS}
            activeStep={activeStep}
            completedSteps={Array.from({ length: activeStep - 1 }, (_, i) => i + 1)}
          />
        </aside>
        <div className="min-w-0 flex-1">
          {activeStep === 1 ? (
            <StepWhatsChanging
              policies={request?.policies ?? policiesFromOrg(org.currentPolicies)}
              kinds={kinds}
              onKindsChange={setKinds}
              saving={creating}
              onNext={leaveFirstStep}
            />
          ) : !request ? (
            <SkeletonRows className="mt-10" rows={6} />
          ) : activeStep === 2 ? (
            <StepDetails
              orgId={orgId}
              request={request}
              kinds={kinds}
              onBack={() => setActiveStep(1)}
              onNext={() => setActiveStep(3)}
            />
          ) : activeStep === 3 ? (
            <StepDates
              orgId={orgId}
              request={request}
              onBack={() => setActiveStep(2)}
              onNext={() => setActiveStep(4)}
            />
          ) : (
            <StepReview
              orgId={orgId}
              request={request}
              onBack={() => setActiveStep(3)}
              forCustomer={!!forCustomer}
            />
          )}
          {request && (
            // The step shell's column, so the row lines up with the step's buttons.
            <div className="mx-auto w-full max-w-2xl px-4 pb-6">
              <WithdrawRequest orgId={orgId} request={request} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
